import React, { useState, useContext, useEffect, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { getCandidateIdentity, resendVerification } from 'recruit/api/candidate_auth';
import { RBannerConfig } from 'recruit/components';
import { Environment } from 'environmentVariables';

export interface RecruitCandidateResume {
    id: number;
    filename: string;
    presigned_url: string;
}

export interface RecruitCandidatePrimaryAddress {
    id: number;
    street: string;
    apt_no_prefix: string;
    apt_no: string;
    city: string;
    state: string;
    zip_code: string;
}

export interface RecruitCandidateUser {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    phone_verified: boolean;
    is_email_verified: boolean;
    role: string;
    resume: RecruitCandidateResume | null;
    bio: string;
    title: string;
    primary_address: RecruitCandidatePrimaryAddress | null;
    profile_picture_url: string | null;
}

export interface PendingSignup {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
}

export type VerifiedVia = 'sms' | 'email';

export interface RecruitCandidateUserContextType {
    candidateUser?: RecruitCandidateUser;
    refreshCandidateUser: () => Promise<RecruitCandidateUser | undefined>;
    logout: () => void;
    isLoading: boolean;
    pendingSignup?: PendingSignup;
    setPendingSignup: (data: PendingSignup | undefined) => void;
    verifiedVia?: VerifiedVia;
    setVerifiedVia: (method: VerifiedVia | undefined) => void;
}

const STORAGE_KEY = 'recruit_token';

export const RecruitCandidateUserContext = React.createContext<RecruitCandidateUserContextType>(
    {} as RecruitCandidateUserContextType,
);

export function getRecruitToken() {
    return localStorage.getItem(STORAGE_KEY);
}

export function setRecruitToken(token: string) {
    localStorage.setItem(STORAGE_KEY, token);
}

export function RecruitCandidateUserProvider({ children }: { children: ReactNode }) {
    const [candidateUser, setCandidateUser] = useState<RecruitCandidateUser>();
    const [isLoading, setIsLoading] = useState(!!getRecruitToken());
    const [pendingSignup, setPendingSignup] = useState<PendingSignup>();
    const [verifiedVia, setVerifiedVia] = useState<VerifiedVia>();

    useEffect(() => {
        if (getRecruitToken()) {
            refreshCandidateUser().finally(() => setIsLoading(false));
        }
    }, []);

    async function refreshCandidateUser() {
        try {
            const token = getRecruitToken();
            if (!token) return undefined;
            const data = await getCandidateIdentity(token);
            setCandidateUser(data);
            return data;
        } catch {
            if (Environment.toLowerCase() !== 'development') {
                localStorage.removeItem(STORAGE_KEY);
                setCandidateUser(undefined);
                return undefined;
            }
            return candidateUser;
        }
    }

    function logout() {
        localStorage.removeItem(STORAGE_KEY);
        setCandidateUser(undefined);
        setPendingSignup(undefined);
        setVerifiedVia(undefined);
    }

    return (
        <RecruitCandidateUserContext.Provider
            value={{
                candidateUser,
                refreshCandidateUser,
                logout,
                isLoading,
                pendingSignup,
                setPendingSignup,
                verifiedVia,
                setVerifiedVia,
            }}
        >
            {children}
        </RecruitCandidateUserContext.Provider>
    );
}

export function useRecruitCandidateUser() {
    const context = useContext(RecruitCandidateUserContext);
    if (context === undefined) {
        throw new Error('useRecruitCandidateUser must be used within a RecruitCandidateUserProvider.');
    }
    return context;
}

export function useVerificationBanner(): RBannerConfig | undefined {
    const { candidateUser } = useRecruitCandidateUser();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const history = useHistory();

    if (!candidateUser) return undefined;

    const { phone_verified, is_email_verified } = candidateUser;
    if (phone_verified && is_email_verified) return undefined;

    let message = '';
    const channels: ('sms' | 'email')[] = [];
    if (!phone_verified && !is_email_verified) {
        message = 'Please verify your phone number and email address.';
        channels.push('sms', 'email');
    } else if (!phone_verified) {
        message = 'Please verify your phone number.';
        channels.push('sms');
    } else {
        message = 'Please verify your email address.';
        channels.push('email');
    }

    async function handleSend() {
        if (sending) return;
        setSending(true);
        setSent(false);
        try {
            const token = getRecruitToken() || undefined;
            const needsSms = channels.includes('sms');
            for (const channel of channels) {
                await resendVerification(channel, token);
            }
            if (needsSms) {
                history.push('/recruit-candidate/verify');
            } else {
                setSent(true);
            }
        } catch {
            setSent(false);
        } finally {
            setSending(false);
        }
    }

    return {
        message,
        variant: 'warning' as const,
        badge: 'Action Required',
        action: (
            <SendLink onClick={handleSend} disabled={sending}>
                {sent ? 'Sent!' : sending ? 'Sending...' : 'Send verification'}
            </SendLink>
        ),
    };
}

const SendLink = styled('button')({
    background: 'none',
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    color: Colors.primary,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
});
